import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AuthSection from '../components/AuthSection.jsx';
import DashboardLayout from '../components/dashboard/DashboardLayout.jsx';
import { RestaurantsSelectView } from '../components/dashboard/RestaurantsSelect.jsx';
import WeeksSelector from '../components/dashboard/WeeksSelector.jsx';
import ProcessSelector from '../components/dashboard/ProcessSelector.jsx';
import GenerateButton from '../components/dashboard/GenerateButton.jsx';
import ToastContainer from '../components/ToastContainer.jsx';
import ForecastConfirmationModal from '../components/dashboard/ForecastConfirmationModal.jsx';
import ProductionSummary from '../components/dashboard/ProductionSummary.jsx';
import KpiCards from '../components/dashboard/KpiCards.jsx';
import ProductionModal from '../components/dashboard/ProductionModal.jsx';
import HourlySalesModal from '../components/dashboard/HourlySalesModal.jsx';
import CategoryAdjustmentModal from '../components/dashboard/CategoryAdjustmentModal.jsx';
import GamificationDashboard from '../components/gamification/GamificationDashboard.jsx';
import { useBootstrap } from '../context/BootstrapContext.jsx';
import { usePageStyles, useLucideIcons } from '../hooks/usePageAssets.js';
import { useClock } from '../hooks/useClock.js';
import { ClockHeaderControls, HoursAdjuster } from '../components/dashboard/ClockControls.jsx';
import { resolveStaticUrl } from '../utils/staticAssets.js';
import { useRestaurants } from '../hooks/useRestaurants.js';
import { useProcessesSelection } from '../hooks/useProcessesSelection.js';
import { useForecastGeneration } from '../hooks/useForecastGeneration.js';
import { buildDetailedTable, buildForecastCsvRows, buildForecastSavePayload, buildHourlySalesData, buildProductionSummary } from '../utils/forecastCalculations.js';
import { exportDocumentFile, groupDetailRecords } from '../utils/exportDocument.js';

const getDefaultPlaceholder = (bootstrap) => {
  if (!bootstrap || bootstrap.ready === false) {
    return { variant: 'welcome' };
  }
  if (!bootstrap.authenticated) {
    return {
      variant: 'login',
      title: 'Acceso restringido',
      message: 'Inicia sesión con tu cuenta autorizada para generar pronósticos.',
      icon: 'key'
    };
  }
  return { variant: 'welcome' };
};

const DashboardPage = () => {
  const bootstrap = useBootstrap();
  const containerRef = useRef(null);
  const exportContainerRef = useRef(null);
  const isSuperAdmin = Boolean(bootstrap?.is_super_admin);
  const [weeks, setWeeks] = useState(bootstrap?.weeks || []);
  const [defaultWeeks, setDefaultWeeks] = useState(bootstrap?.default_weeks || 2);
  const [processesData, setProcessesData] = useState(bootstrap?.processes_data || []);
  const visibleProcessesData = useMemo(() => {
    if (!Array.isArray(processesData)) {
      return [];
    }
    if (isSuperAdmin) {
      return processesData;
    }
    return processesData.filter((process) => !process?.proceso_es_beta);
  }, [processesData, isSuperAdmin]);
  const weeksCount = Array.isArray(weeks) ? weeks.length : 0;
  usePageStyles([resolveStaticUrl('css/style.css')]);
  const clock = useClock();
  const [hoursOverride, setHoursOverride] = useState(null);
  const restaurantsState = useRestaurants();
  const {
    items: restaurantItems,
    loading: restaurantsLoading,
    error: restaurantsError,
    selected: selectedRestaurant,
    disabled: restaurantsDisabled,
    isSingle: isSingleRestaurant,
    setSelected: setSelectedRestaurant,
    pendingDispatch,
    details: restaurantDetails
  } = restaurantsState;
  const [selectedWeeks, setSelectedWeeks] = useState(new Set());
  const [weeksError, setWeeksError] = useState(null);
  const {
    sections: processSections,
    selection: selectedProcesses,
    toggle: toggleProcess
  } = useProcessesSelection(visibleProcessesData, { allowWarmer: Boolean(restaurantDetails?.warmer) });
  const warmerName = useMemo(() => {
    if (!restaurantDetails?.warmer) {
      return null;
    }
    return restaurantDetails?.warmer_nombre || `Warmer ${restaurantDetails.warmer}`;
  }, [restaurantDetails]);
  const autoCalculation = Boolean(restaurantDetails?.calculo_automatico);
  const minWeeksRequired = autoCalculation ? 1 : 2;

  const [viewState, setViewState] = useState('placeholder');
  const [placeholderState, setPlaceholderState] = useState(() => getDefaultPlaceholder(bootstrap));
  const [dashboardFilter, setDashboardFilter] = useState('all');
  const [detailedTableData, setDetailedTableData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [forecastGenerationTime, setForecastGenerationTime] = useState(null);
  const [categoryAdjustments, setCategoryAdjustments] = useState({});
  const [productionItem, setProductionItem] = useState(null);
  const [productionItemKey, setProductionItemKey] = useState(null);
  const [adjustCategoryTarget, setAdjustCategoryTarget] = useState(null);
  const [stockOverrides, setStockOverrides] = useState({});
  const [hourlyModalData, setHourlyModalData] = useState(null);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [saveControls, setSaveControls] = useState({
    visible: false,
    hasUnsavedChanges: false,
    forecastAvailable: false,
    lastSavedHistoryId: null,
    snapshotUuid: null
  });
  const [saveStatus, setSaveStatus] = useState('idle');
  const [exportingFormat, setExportingFormat] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = toastTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimers.current.delete(id);
    }
  }, []);
  const pushToast = useCallback((message, type = 'info') => {
    if (!message) {
      return;
    }
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    const timer = setTimeout(() => dismissToast(id), 4000);
    toastTimers.current.set(id, timer);
  }, [dismissToast]);
  const handleStockChange = useCallback((articleId, value) => {
    setStockOverrides((prev) => ({
      ...prev,
      [articleId]: value
    }));
    setSaveControls((prev) => ({
      ...prev,
      hasUnsavedChanges: true,
      lastSavedHistoryId: null
    }));
  }, []);

  const handleOpenProduction = useCallback((item) => {
    if (!item) {
      return;
    }
    if (item.isAzucarGlass) {
      return;
    }
    setProductionItem(item);
    setProductionItemKey(item.name);
  }, []);

  const handleCloseProduction = useCallback(() => {
    setProductionItem(null);
    setProductionItemKey(null);
  }, []);

  const handleAdjustCategory = useCallback((group) => {
    if (!group) {
      return;
    }
    setAdjustCategoryTarget({
      key: group.key,
      name: group.name,
      categoryType: group.categoryType,
      adjustmentDetails: group.adjustmentDetails || null
    });
  }, []);

  const handleApplyCategoryAdjustment = useCallback((payload) => {
    if (!payload?.categoryKey) {
      return;
    }
    setCategoryAdjustments((prev) => {
      const next = { ...prev };
      const adjustments = (payload.adjustments || []).filter((item) => Number.isFinite(item.value) && item.value !== 0);
      const total = adjustments.reduce((sum, item) => sum + (item.value || 0), 0);
      if (!adjustments.length || total === 0) {
        delete next[payload.categoryKey];
      } else {
        next[payload.categoryKey] = {
          motivoId: adjustments[0].motivoId,
          motivoNombre: adjustments[0].motivoNombre,
          percentage: total,
          adjustmentsByMotivo: adjustments,
          categoryType: payload.categoryType,
          categoryDisplayName: payload.categoryName,
          timestamp: new Date().toISOString()
        };
      }
      return next;
    });
    setSaveControls((prev) => ({
      ...prev,
      hasUnsavedChanges: true,
      lastSavedHistoryId: null
    }));
  }, []);

  const handleViewHourly = useCallback((articleId) => {
    if (!articleId) {
      return;
    }
    if (!forecastData) {
      pushToast('Genera un pronóstico antes de revisar el detalle por horas.', 'warning');
      return;
    }
    setHourlyLoading(true);
    const payload = buildHourlySalesData(forecastData, {
      articleName: articleId,
      simulationTime: clock.time,
      hoursOverride,
      categoryAdjustments
    });
    if (!payload || !payload.rows.length) {
      pushToast('No se encontraron datos horarios para este artículo.', 'warning');
      setHourlyLoading(false);
      return;
    }
    setHourlyModalData(payload);
    setHourlyLoading(false);
  }, [forecastData, clock.time, hoursOverride, categoryAdjustments, pushToast]);

  const handleSaveForecastClick = useCallback(async () => {
    if (saveStatus === 'saving') {
      return;
    }
    if (!saveControls.forecastAvailable || !forecastData || !lastPayload) {
      pushToast('Genera un pronóstico antes de guardarlo.', 'warning');
      return;
    }
    const payload = buildForecastSavePayload({
      forecastData,
      queryParams: lastPayload,
      stockOverrides,
      categoryAdjustments,
      hoursOverride,
      simulationTime: clock.time,
      snapshotUuid: saveControls.snapshotUuid
    });
    if (!payload) {
      pushToast('No se pudo preparar la información del pronóstico.', 'warning');
      return;
    }
    setSaveStatus('saving');
    try {
      const response = await fetch('/api/save_forecast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        data = {};
      }
      if (!response.ok || data?.error) {
        throw new Error(data?.error || `Error ${response.status}`);
      }
      setSaveControls((prev) => ({
        ...prev,
        hasUnsavedChanges: false,
        forecastAvailable: true,
        lastSavedHistoryId: data.history_id || prev.lastSavedHistoryId
      }));
      setSaveStatus('success');
      pushToast('Pronóstico guardado correctamente.', 'success');
    } catch (error) {
      pushToast(error?.message || 'No se pudo guardar el pronóstico.', 'error');
      setSaveStatus('error');
    }
  }, [
    saveStatus,
    saveControls.forecastAvailable,
    saveControls.snapshotUuid,
    forecastData,
    lastPayload,
    stockOverrides,
    categoryAdjustments,
    hoursOverride,
    clock.time,
    pushToast
  ]);

  const exportReady = Boolean(saveControls.lastSavedHistoryId);
  const forecastRestaurantName = useMemo(() => {
    const candidates = [
      lastPayload?.sucursal,
      restaurantDetails?.nombre,
      restaurantDetails?.sucursal_nombre,
      restaurantDetails?.sucursal,
      selectedRestaurant
    ];
    return candidates.find((value) => typeof value === 'string' && value.trim()) || '';
  }, [lastPayload?.sucursal, restaurantDetails?.nombre, restaurantDetails?.sucursal_nombre, restaurantDetails?.sucursal, selectedRestaurant]);

  const handleExportRequest = useCallback((format) => {
    if (!format) {
      return;
    }
    if (!saveControls.lastSavedHistoryId) {
      pushToast('Guarda el pronóstico antes de exportarlo.', 'warning');
      return;
    }
    setExportingFormat(format);
    setExporting(true);
    const runExport = async () => {
      try {
        await new Promise((resolve) => {
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => resolve());
            return;
          }
          setTimeout(resolve, 0);
        });
        const response = await fetch(`/api/forecast_history/${saveControls.lastSavedHistoryId}`);
        if (!response.ok) {
          throw new Error('No se pudo obtener el detalle del pronóstico.');
        }
        const payload = await response.json();
        const detailGroups = groupDetailRecords(payload.details || []);
        const verificationUrl = `${window.location.origin}/history/verify?h=${encodeURIComponent(payload.header.id)}&s=${encodeURIComponent(payload.header.snapshot_uuid || '')}`;
        await exportDocumentFile({
          container: exportContainerRef.current,
          header: payload.header,
          detailGroups,
          verificationUrl,
          format
        });
        const formatLabel = format === 'pdf' ? 'PDF' : 'JPG';
        pushToast(`Se generó el ${formatLabel} del pronóstico.`, 'success');
      } catch (error) {
        pushToast(error?.message || 'No se pudo exportar el pronóstico.', 'error');
      } finally {
        setExporting(false);
        setExportingFormat(null);
      }
    };
    runExport();
  }, [pushToast, saveControls.lastSavedHistoryId]);

  const handleExportForecastCsv = useCallback(() => {
    if (!isSuperAdmin) {
      pushToast('Solo los super administradores pueden exportar el forecast en CSV.', 'warning');
      return;
    }
    if (!saveControls.lastSavedHistoryId) {
      pushToast('Guarda el pronóstico antes de exportarlo.', 'warning');
      return;
    }
    if (!forecastData) {
      pushToast('Genera un pronóstico antes de exportarlo.', 'warning');
      return;
    }
    setExportingCsv(true);
    try {
      const rows = buildForecastCsvRows(forecastData, {
        simulationTime: clock.time,
        hoursOverride,
        categoryAdjustments,
        restaurantName: forecastRestaurantName
      });
      if (!rows.length) {
        pushToast('No hay datos horarios para exportar.', 'warning');
        return;
      }
      const headers = [
        'Nombre del restaurante',
        'Tipo de Item',
        'Nombre del Item',
        'Dia',
        'Hora inicial',
        'Hora final',
        'Cantidades pronosticadas',
        'Unidad'
      ];
      const escapeCsv = (value) => {
        if (value === null || value === undefined) {
          return '';
        }
        const str = String(value);
        if (str.includes('"') || str.includes(',') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      const lines = [
        headers.map(escapeCsv).join(',')
      ];
      rows.forEach((row) => {
        lines.push([
          row.restaurantName || forecastRestaurantName,
          row.itemType,
          row.itemName,
          row.day,
          row.startTime,
          row.endTime,
          row.quantity,
          (row.unit || '').toString().toUpperCase()
        ].map(escapeCsv).join(','));
      });
      const csvContent = `\ufeff${lines.join('\n')}`;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date();
      const safeRestaurant = (forecastRestaurantName || rows[0]?.restaurantName || 'restaurante')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '_')
        .replace(/^_+|_+$/g, '');
      link.href = url;
      link.download = `forecast_${safeRestaurant || 'restaurante'}_${timestamp.toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      pushToast('Se exportó el forecast en CSV.', 'success');
    } catch (error) {
      pushToast(error?.message || 'No se pudo exportar el forecast a CSV.', 'error');
    } finally {
      setExportingCsv(false);
    }
  }, [
    isSuperAdmin,
    saveControls.lastSavedHistoryId,
    forecastData,
    clock.time,
    hoursOverride,
    categoryAdjustments,
    forecastRestaurantName,
    pushToast
  ]);
  useEffect(() => {
    setDashboardFilter('all');
  }, [summaryData]);

  useEffect(() => {
    setPlaceholderState(getDefaultPlaceholder(bootstrap));
  }, [bootstrap?.authenticated, bootstrap?.ready]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const container = document.createElement('div');
    container.id = 'export-document';
    Object.assign(container.style, {
      position: 'fixed',
      top: '-9999px',
      left: '-9999px',
      zIndex: '0'
    });
    document.body.appendChild(container);
    exportContainerRef.current = container;
    return () => {
      exportContainerRef.current = null;
      if (container.parentElement) {
        container.parentElement.removeChild(container);
      }
    };
  }, []);

  useEffect(() => {
    if (!restaurantDetails) {
      setProcessesData(bootstrap?.processes_data || []);
      setWeeks(bootstrap?.weeks || []);
      setDefaultWeeks(bootstrap?.default_weeks || 2);
      return;
    }
    if (Array.isArray(restaurantDetails.processes_data)) {
      setProcessesData(restaurantDetails.processes_data);
    }
    if (Array.isArray(restaurantDetails.weeks) && restaurantDetails.weeks.length) {
      setWeeks(restaurantDetails.weeks);
    }
    if (typeof restaurantDetails.default_weeks === 'number') {
      setDefaultWeeks(restaurantDetails.default_weeks);
    }
  }, [restaurantDetails, bootstrap?.processes_data, bootstrap?.weeks, bootstrap?.default_weeks]);

  useEffect(() => {
    if (!forecastData) {
      setSummaryData(null);
      setDetailedTableData(null);
      if (viewState === 'data') {
        setViewState('placeholder');
        setPlaceholderState(getDefaultPlaceholder(bootstrap));
      }
      return;
    }
    const summary = buildProductionSummary(forecastData, {
      simulationTime: clock.time,
      hoursOverride,
      stockOverrides,
      categoryAdjustments
    });
    setSummaryData(summary);
    const detailed = buildDetailedTable(forecastData, {
      simulationTime: clock.time,
      hoursOverride,
      categoryAdjustments
    });
    setDetailedTableData(detailed);
    setViewState('data');
  }, [
    forecastData,
    clock.time,
    hoursOverride,
    stockOverrides,
    categoryAdjustments,
    viewState,
    bootstrap
  ]);

  useEffect(() => {
    if (!productionItemKey || !summaryData?.groups?.length) {
      return;
    }
    const updated = summaryData.groups
      .flatMap((group) => group.items || [])
      .find((item) => item.name === productionItemKey);
    if (updated) {
      setProductionItem(updated);
    }
  }, [summaryData, productionItemKey]);

  useEffect(() => {
    if (saveStatus === 'success' || saveStatus === 'error') {
      const timer = setTimeout(() => setSaveStatus('idle'), 2500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [saveStatus]);

  useEffect(() => {
    if (saveControls.hasUnsavedChanges && saveStatus === 'success') {
      setSaveStatus('idle');
    }
  }, [saveControls.hasUnsavedChanges, saveStatus]);

  useEffect(() => {
    if (viewState === 'data') {
      const parsed = lastPayload?.simulation_time ? new Date(lastPayload.simulation_time) : null;
      const validSimulationTime = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
      if (validSimulationTime) {
        setForecastGenerationTime(validSimulationTime);
        clock.freezeClock(validSimulationTime);
        return;
      }
      setForecastGenerationTime(null);
      clock.freezeClock();
      return;
    }
    setForecastGenerationTime(null);
    clock.resumeClock();
  }, [viewState, lastPayload?.simulation_time, clock.freezeClock, clock.resumeClock]);

  useEffect(() => () => {
    toastTimers.current.forEach((timer) => clearTimeout(timer));
    toastTimers.current.clear();
  }, []);

  const adminUrl = bootstrap?.routes?.admin || '/admin';
  const historyUrl = bootstrap?.routes?.history || '/history';

  const processesJson = useMemo(
    () => JSON.stringify(visibleProcessesData).replace(/</g, '\\u003c'),
    [visibleProcessesData]
  );

  const historyButton = bootstrap?.user_profile ? (
    <a href={historyUrl} className="primary-action-btn history-btn">
      <span className="btn-icon">
        <i data-lucide="clock" />
      </span>
      <span className="btn-text">Historial</span>
    </a>
  ) : null;

  const errorBanner = bootstrap?.error_message ? (
    <div className="error-banner" style={{ margin: '16px auto', maxWidth: '1200px', padding: '12px 16px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fee2e2', color: '#991b1b' }}>
      {bootstrap.error_message}
    </div>
  ) : null;

  const handleWeeksSelectionChange = (values, error) => {
    setSelectedWeeks(new Set(values || []));
    setWeeksError(error);
  };

  const canGenerate =
    Boolean(selectedRestaurant) &&
    selectedProcesses.size > 0 &&
    selectedWeeks.size >= minWeeksRequired &&
    !weeksError;

  const handleForecastStart = useCallback((payload) => {
    setViewState('loading');
    setPlaceholderState({
      variant: 'loading',
      title: 'Generando pronóstico',
      message: 'Esto tomará solo unos segundos.',
      icon: 'loader'
    });
    setForecastData(null);
    setSummaryData(null);
    setDetailedTableData(null);
    setHourlyModalData(null);
    setProductionItem(null);
    setProductionItemKey(null);
    setLastPayload(payload);
    setSaveControls((prev) => ({
      ...prev,
      visible: true,
      forecastAvailable: false,
      hasUnsavedChanges: true,
      lastSavedHistoryId: null,
      snapshotUuid: payload?.snapshot_uuid || payload?.snapshotUuid || prev.snapshotUuid
    }));
  }, []);

  const handleForecastSuccess = useCallback(({ payload, data }) => {
    const normalized = {
      forecastAverages: data?.forecast_averages || [],
      selectedArticulos: data?.selected_articulos || [],
      modificadores: payload?.modificadores || { clima: 0, promo: 0, evento: 0 }
    };
    setForecastData(normalized);
    setLastPayload(payload);
    setViewState('data');
    setPlaceholderState(null);
    setSaveControls((prev) => ({
      ...prev,
      visible: true,
      forecastAvailable: true,
      hasUnsavedChanges: true,
      snapshotUuid: payload?.snapshot_uuid || payload?.snapshotUuid || prev.snapshotUuid
    }));
    setStockOverrides({});
    setProductionItem(null);
    setProductionItemKey(null);
    setHourlyModalData(null);
  }, []);

  const handleForecastError = useCallback((message) => {
    const detail = message || 'Error al generar el pronóstico.';
    setViewState('placeholder');
    setPlaceholderState({
      variant: 'error',
      title: 'No se pudo generar el pronóstico',
      message: detail,
      icon: 'alert-triangle'
    });
    setForecastData(null);
    pushToast(detail, 'error');
  }, [pushToast]);

  const warmerProcesses = useMemo(
    () => (Array.isArray(visibleProcessesData) ? visibleProcessesData.filter((p) => p?.proceso_es_warmer) : []),
    [visibleProcessesData]
  );
  const azucarGlassProcesses = useMemo(
    () => (Array.isArray(visibleProcessesData) ? visibleProcessesData.filter((p) => p?.proceso_es_azucar_glass) : []),
    [visibleProcessesData]
  );
  const hasSelectedWarmerProcess = useMemo(
    () => warmerProcesses.some((p) => selectedProcesses.has(p.proceso)),
    [warmerProcesses, selectedProcesses]
  );
  const hasSelectedAzucarGlassOnly = useMemo(() => {
    if (!selectedProcesses || selectedProcesses.size === 0) {
      return false;
    }
    const azucarSet = new Set(azucarGlassProcesses.map((process) => process.proceso));
    if (azucarSet.size === 0) {
      return false;
    }
    const selectedArray = Array.from(selectedProcesses);
    const hasAzucar = selectedArray.some((processName) => azucarSet.has(processName));
    if (!hasAzucar) {
      return false;
    }
    return selectedArray.every((processName) => azucarSet.has(processName));
  }, [azucarGlassProcesses, selectedProcesses]);

  const {
    status: generationStatus,
    confirmationOpen,
    openConfirmation,
    closeConfirmation,
    confirmGeneration
  } = useForecastGeneration({
    restaurant: selectedRestaurant,
    processesSelection: selectedProcesses,
    weeksSelection: selectedWeeks,
    weeksError,
    canGenerate,
    simulationTime: clock.time,
    isManualTime: clock.isManual,
    hoursOverride,
    onHoursOverrideChange: setHoursOverride,
    processesMeta: visibleProcessesData,
    onStart: handleForecastStart,
    onSuccess: handleForecastSuccess,
    onError: handleForecastError,
    onWarning: (message) => pushToast(message, 'warning'),
    warmerMode: hasSelectedWarmerProcess,
    azucarGlassMode: hasSelectedAzucarGlassOnly,
    isSuperAdmin
  });

  useLucideIcons([
    viewState,
    placeholderState?.variant,
    toasts.length,
    summaryData,
    detailedTableData,
    productionItem,
    hourlyModalData,
    saveControls.visible,
    saveControls.hasUnsavedChanges,
    saveStatus,
    hasSelectedWarmerProcess
  ]);

  useEffect(() => {
    if (hasSelectedWarmerProcess && hoursOverride !== null) {
      setHoursOverride(null);
    }
  }, [hasSelectedWarmerProcess, hoursOverride]);

  const handleManualTimeApply = useCallback((nextTime) => {
    if (!forecastData) {
      return;
    }
    if (!canGenerate) {
      pushToast('Configura los parámetros antes de recalcular el pronóstico.', 'warning');
      return;
    }
    if (generationStatus?.loading) {
      pushToast('Espera a que termine el cálculo en progreso.', 'info');
      return;
    }
    const resolvedMode = hasSelectedWarmerProcess
      ? 'lifespan'
      : (hasSelectedAzucarGlassOnly ? 'hours' : (typeof hoursOverride === 'number' ? 'hours' : 'lifespan'));
    confirmGeneration({
      forecastTime: nextTime || clock.time,
      mode: resolvedMode,
      hoursValue: hasSelectedWarmerProcess ? '' : (hoursOverride ?? '')
    });
  }, [
    forecastData,
    canGenerate,
    generationStatus?.loading,
    confirmGeneration,
    hoursOverride,
    clock.time,
    hasSelectedWarmerProcess,
    hasSelectedAzucarGlassOnly,
    pushToast
  ]);

  const processEmptyMessage = restaurantsLoading
    ? 'Cargando procesos...'
    : selectedRestaurant
      ? 'No hay procesos disponibles'
      : 'Seleccione un restaurante para ver los procesos...';

  useEffect(() => {
    if (typeof window === 'undefined' || !window.console) {
      return;
    }
    const processesCount = Array.isArray(visibleProcessesData) ? visibleProcessesData.length : 0;
    window.console.info('[Diagnóstico] Estado actual', {
      selectedRestaurant,
      processes: processesCount,
      weeks: weeksCount,
      processSections: processSections.length,
      warmer: restaurantDetails?.warmer,
      allowWarmer: Boolean(restaurantDetails?.warmer),
      processesData: visibleProcessesData,
      processSections: processSections
    });
    
    if (processesCount > 0 && processSections.length === 0) {
      window.console.warn('[Diagnóstico] Procesos presentes pero no agrupados. ¿Todos requieren warmer?', {
        allowWarmer: Boolean(restaurantDetails?.warmer),
        processesData: visibleProcessesData,
        sample: visibleProcessesData.slice(0, 5)
      });
    }
    
    if (!weeksCount) {
      window.console.warn('[Diagnóstico] No se recibieron semanas.');
    }
  }, [visibleProcessesData, processSections, weeksCount, selectedRestaurant, restaurantDetails?.warmer]);

  const summaryTitle = summaryData?.title || 'Unidades a producir de acuerdo a tiempo de vida';
  const summarySubtitle = summaryData?.subtitle || 'Resumen visual de producción requerida';
  const summaryContent = (
    <ProductionSummary
      data={summaryData}
      onSelectItem={handleOpenProduction}
      onAdjustCategory={handleAdjustCategory}
    />
  );
  const additionalInfoContent = useMemo(() => {
    if (!forecastData || !selectedProcesses?.size || !Array.isArray(visibleProcessesData)) {
      return null;
    }
    const selectedArray = Array.from(selectedProcesses);
    const infoItems = selectedArray
      .map((name) => visibleProcessesData.find((process) => process.proceso === name))
      .filter((process) => process?.info_html);
    if (!infoItems.length) {
      return null;
    }
    return infoItems.map((process) => (
      <div className="additional-info-card" key={`info-${process.proceso_id || process.proceso}`}>
        <h4 className="additional-info-title">{process.proceso}</h4>
        <div
          className="additional-info-body"
          dangerouslySetInnerHTML={{ __html: process.info_html }}
        />
      </div>
    ));
  }, [forecastData, selectedProcesses, visibleProcessesData]);
  const kpiContent = (
    <KpiCards stats={summaryData?.stats} />
  );
  const showWarmerBanner = viewState === 'data' && hasSelectedWarmerProcess;
  const showManualLegend = useMemo(() => {
    if (!forecastGenerationTime || !clock.isManual) {
      return false;
    }
    const diffMs = Math.abs(clock.time.getTime() - forecastGenerationTime.getTime());
    return diffMs > 2 * 60 * 1000;
  }, [forecastGenerationTime, clock.isManual, clock.time]);
  const clockDisplayLabel = useMemo(() => {
    if (typeof clock.formatLabel === 'function') {
      return clock.formatLabel(showManualLegend);
    }
    return clock.label;
  }, [clock.formatLabel, clock.label, showManualLegend]);
  const hoursControl = hasSelectedWarmerProcess ? (
    <div className="new-hours-control">
      <label className="new-hours-label">Calcular pronóstico basado en</label>
      <div className="input-wrapper">
        <i data-lucide="flame" className="input-icon" />
        <input
          type="text"
          className="new-hours-input"
          value="De acuerdo a tiempo de vida de warmer"
          readOnly
          disabled
        />
      </div>
      <p className="muted-text" style={{ marginTop: '0.35rem' }}>
        Se usa el tiempo de vida configurado en la receta del warmer asignado al restaurante.
      </p>
    </div>
  ) : (
    <HoursAdjuster
      value={hoursOverride}
      maxHours={hasSelectedAzucarGlassOnly ? 24 : 12}
      onApply={(value) => {
        setHoursOverride(value);
        setSaveControls((prev) => ({
          ...prev,
          hasUnsavedChanges: true,
          lastSavedHistoryId: null
        }));
      }}
      onReset={() => {
        setHoursOverride(null);
        setSaveControls((prev) => ({
          ...prev,
          hasUnsavedChanges: true,
          lastSavedHistoryId: null
        }));
      }}
    />
  );

  if (!bootstrap?.authenticated) {
    return (
      <div id="dashboard-page" className="app-shell">
        <header className="main-header">
          <div className="header-left">
            <a href="/">
              <img src={resolveStaticUrl('images/logo.png')} alt="Tim Hortons" className="header-logo" />
            </a>
            <h1 className="system-title">Hat Trick</h1>
          </div>
          <div className="header-right">
            <AuthSection />
          </div>
        </header>
        <main className="landing-empty">
          <h2>Necesitas iniciar sesión</h2>
          <p>Accede con tu cuenta autorizada para generar pronósticos.</p>
        </main>
      </div>
    );
  }

  return (
    <div id="dashboard-page" ref={containerRef}>
      <DashboardLayout
        authSection={<AuthSection />}
        historyButton={historyButton}
        warmerName={warmerName}
        showWarmerBanner={showWarmerBanner}
        weeksCheckboxes={selectedRestaurant ? (
          <WeeksSelector
            weeks={weeks}
            defaultWeeks={defaultWeeks}
            minWeeks={minWeeksRequired}
            autoMode={autoCalculation}
            onSelectionChange={handleWeeksSelectionChange}
          />
        ) : null}
        errorBanner={errorBanner}
        kpiContent={kpiContent}
        restaurantSelect={
          <RestaurantsSelectView
            items={restaurantItems}
            loading={restaurantsLoading}
            error={restaurantsError}
            selected={selectedRestaurant}
            disabled={restaurantsDisabled}
            isSingle={isSingleRestaurant}
            onChange={setSelectedRestaurant}
            pendingDispatch={pendingDispatch}
          />
        }
        processSelector={
          <>
            <ProcessSelector
              sections={processSections}
              selected={selectedProcesses}
              onToggle={toggleProcess}
              disabled={!selectedRestaurant}
              emptyMessage={processEmptyMessage}
            />
          </>
        }
        generateButton={(
          <GenerateButton
            canGenerate={canGenerate}
            onClick={openConfirmation}
            status={generationStatus}
          />
        )}
        clockHeader={<ClockHeaderControls clock={clock} onApply={handleManualTimeApply} displayLabel={clockDisplayLabel} />}
        hoursControl={hoursControl}
        viewState={viewState}
        placeholderState={placeholderState}
        dashboardTable={summaryData}
        dashboardStocks={stockOverrides}
        onDashboardStockChange={handleStockChange}
        dashboardTableFilter={dashboardFilter}
        onDashboardFilterChange={setDashboardFilter}
        detailedTable={detailedTableData}
        summaryTitle={summaryTitle}
        summarySubtitle={summarySubtitle}
        summaryContent={summaryContent}
        gamificationContent={(
          <GamificationDashboard
            authenticated={Boolean(bootstrap?.authenticated)}
            restaurantItems={restaurantItems}
          />
        )}
        additionalInfoContent={additionalInfoContent}
        forecastModal={(
          <ForecastConfirmationModal
            open={confirmationOpen}
            onClose={closeConfirmation}
            onConfirm={confirmGeneration}
            defaultTime={clock.time}
            defaultHours={hasSelectedWarmerProcess ? null : hoursOverride}
            isWarmerProcess={hasSelectedWarmerProcess}
            isAzucarGlassProcess={hasSelectedAzucarGlassOnly}
            isSuperAdmin={isSuperAdmin}
          />
        )}
        processesJson={processesJson}
        saveControls={saveControls}
        saveStatus={saveStatus}
        onSaveForecast={handleSaveForecastClick}
        onExportRequest={handleExportRequest}
        onExportForecastCsv={handleExportForecastCsv}
        canExportForecastCsv={isSuperAdmin}
        exporting={exporting}
        exportingFormat={exportingFormat}
        exportingCsv={exportingCsv}
        exportReady={exportReady}
        azucarGlassMode={hasSelectedAzucarGlassOnly}
      />
      <ProductionModal
        open={Boolean(productionItem)}
        item={productionItem}
        onClose={handleCloseProduction}
        onApplyStock={handleStockChange}
        onViewHourly={handleViewHourly}
      />
      <CategoryAdjustmentModal
        open={Boolean(adjustCategoryTarget)}
        category={adjustCategoryTarget}
        onClose={() => setAdjustCategoryTarget(null)}
        onApply={handleApplyCategoryAdjustment}
      />
      <HourlySalesModal
        open={hourlyLoading || Boolean(hourlyModalData)}
        data={hourlyModalData}
        loading={hourlyLoading}
        onClose={() => {
          setHourlyLoading(false);
          setHourlyModalData(null);
        }}
      />
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default DashboardPage;
